import { Modal } from 'antd';
import {
  ICycleResource,
  IMusselSeeding,
} from '../../entities/growing.entities';
import { Button, CloseIcon, Datepicker, Input, Subtitle } from '../shared';
import { translate } from '../../lib/lang.helper';
import { useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { useEffect, useMemo, useState } from 'react';
import {
  getAverageSize,
  getUnitOfSpecie,
  toMillisecond,
  toSecond,
} from '../../lib/common.helpers';
import { sendSingleRequest } from '../../apis';
import { TBusinessType } from '../../entities/general.entities';
import { selectAccount } from '../../store/auth/auth.selector';

interface Props {
  visible: boolean;
  onClose: () => void;
  onUpdate?: (d: any) => void;
  cycleData: ICycleResource;
  cropType: TBusinessType;
}

const HarvestDateModal = ({
  visible,
  onClose,
  onUpdate,
  cycleData,
  cropType,
}: Props) => {
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const lastEstimateAmount =
    cycleData.assessments && cycleData.assessments.length > 0
      ? cycleData.assessments.sort(
          (a, b) =>
            new Date(b.assessment_date).getTime() -
            new Date(a.assessment_date).getTime(),
        )[0].estimated_amount
      : null;

  const [date, setDate] = useState(Date.now());
  const [disabled, setDisabled] = useState(false);
  const [amount, setAmount] = useState(
    lastEstimateAmount !== null ? lastEstimateAmount / 1000 : null,
  );
  const [musselsPerKg, setMusselsPerKg] = useState<number>();

  const confirmClick = async () => {
    setDisabled(true);
    const response = await sendSingleRequest(
      {
        seeding_id: cycleData.main_seed.id,
        planned_date_harvest: toSecond(date),
        estimated_amount: amount ? amount * 1000 : undefined,
      },
      'POST',
      `api/farm/line/assessment/estimate`,
      true,
    );
    if (response.status) {
      if (onUpdate) {
        await onUpdate(response.data);
      }
      onClose();
    } else {
      window.alert(translate(lang, response.data?.message || 'Unknown error'));
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (cycleData.assessments && cycleData.assessments.length > 0) {
      const last = cycleData.assessments.reduce((p, c) =>
        p.assessment_date > c.assessment_date ? p : c,
      );
      setDate(toMillisecond(last.planned_date_harvest));
    } else {
      setDate(toMillisecond(cycleData.main_seed.planned_date_harvest));
    }
  }, [cycleData]);

  const { isPredictable, lineLength, density, densityUnit } = useMemo(() => {
    if (
      cropType !== 'MUSSEL' ||
      !cycleData.main_seed.is_growing ||
      cycleData.assessments.length <= 0
    ) {
      return {
        isPredictable: false,
        lineLength: 0,
        density: 0,
        densityUnit: 'mussels per m' as const,
      };
    }
    const musselSeedings =
      cycleData.crop_type === 'MUSSEL'
        ? (cycleData.seedings as IMusselSeeding[])
        : [];
    const lineLength =
      musselSeedings.reduce((sum, seed) => sum + seed.line_length, 0) -
      cycleData.harvests.reduce(
        (sum, harvest) => sum + (harvest.line_length ?? 0),
        0,
      );
    const lastAssessment =
      cycleData.assessments.length > 0
        ? cycleData.assessments.reduce((p, c) =>
            p.assessment_date > c.assessment_date ? p : c,
          )
        : null;
    const density = lastAssessment?.density
      ? getAverageSize(lastAssessment.density, lastAssessment.density_max)
      : getAverageSize(
          cycleData.main_seed.density,
          cycleData.main_seed.density_max,
        );

    if (lastEstimateAmount && density && lineLength) {
      const mp = Math.round((lineLength * density) / lastEstimateAmount);
      setMusselsPerKg(mp);
    }

    return {
      isPredictable: true,
      lineLength,
      density,
      densityUnit: cycleData.main_seed.density_unit,
    };
  }, [cycleData, cropType]);

  const changeMusselsPerKg = (value: string) => {
    const val = value.length > 0 ? parseFloat(value) : undefined;
    setMusselsPerKg(val);
    if (val !== undefined) {
      const eta = Math.round((lineLength * density) / val / 1000);
      setAmount(eta);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      className='harvest-date-modal'
      closable
      closeIcon={<CloseIcon />}
      width={500}
      footer={null}
    >
      <div className='section-modal wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Estimated harvest date')}
          </Subtitle>
        </div>
        <div className={isPredictable ? 'mb-16' : `mb-32`}>
          <Datepicker
            defaultValue={date}
            label={translate(lang, 'Estimated harvest date')}
            onChange={e => e && setDate(e.toDate().getTime())}
            disabled={disabled}
          />
        </div>
        {isPredictable && (
          <>
            <div className='mb-16'>
              <Input
                label={translate(lang, 'Length')}
                type='number'
                value={lineLength.toString()}
                unit='m'
                disabled={true}
              />
            </div>
            <div className='mb-16'>
              <Input
                label={translate(lang, 'Density')}
                type='number'
                value={density.toString()}
                unit={densityUnit}
                disabled={true}
              />
            </div>
            <div className='mb-16'>
              <Input
                label={translate(lang, 'Performance')}
                type='number'
                value={musselsPerKg?.toString() ?? ''}
                onChange={e => changeMusselsPerKg(e.target.value)}
                unit={`mussels per ${getUnitOfSpecie(unitType, 'weight_kg')}`}
              />
            </div>
            <div className='mb-32'>
              <Input
                label={translate(lang, 'Estimate tonnage')}
                type='number'
                value={amount?.toString() ?? ''}
                onChange={e =>
                  setAmount(e.target.value ? parseFloat(e.target.value) : null)
                }
              />
            </div>
          </>
        )}
        <div className='modal-button d-flex justify-end'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HarvestDateModal;
